// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import {
  anularVacacion,
  confirmarVacacion,
  crearVacacion,
  traerVacaciones,
} from "../services/vacaciones";
import { traerNomina } from "../services/rrhh";
import { rutaDePersona } from "../shared/personas";
import {
  anioVacaciones,
  antiguedadAlCierre,
  diasCorridos,
  estado as estadoDe,
} from "../shared/vacaciones";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Vacaciones (P-172, Natalia): los días que corresponden por antigüedad
 * (CCT 40/89 art. 46), lo tomado y lo pendiente, por empleado.
 *
 * La regla vive en `shared/vacaciones.js` — acá sólo se dibuja. Los días que
 * corresponden salen de la antigüedad (nómina → `ingreso_computable`); lo
 * tomado, de `vacaciones_tomadas`. Sólo lo tildado OK descuenta: una vacación
 * anotada pero sin confirmar todavía no se tomó.
 *
 * Cargar y confirmar piden `cargar.rrhh`; mirar, `ver.rrhh`. Es dato de RRHH.
 */

const fmt = (v) => {
  const s = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

const Vacaciones = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = permisos !== null && permisos.includes("ver.rrhh");
  const puedeCargar = permisos !== null && permisos.includes("cargar.rrhh");

  const anio = anioVacaciones();

  const [nomina, setNomina] = useState([]);
  const [tomadas, setTomadas] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [abierto, setAbierto] = useState(null); // dni desplegado
  const [filtro, setFiltro] = useState("");

  // El alta de un período.
  const [dni, setDni] = useState("");
  const [buscar, setBuscar] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [nota, setNota] = useState("");
  const [estado, setEstado] = useState(""); // "" | yendo | mal:<msg>

  const cargar = () => {
    traerVacaciones(anio).then(setTomadas).catch(() => setError(true));
  };

  useEffect(() => {
    if (!permitido) return;
    cargar();
    traerNomina()
      .then((filas) => setNomina((filas || []).filter((f) => f.trabaja)))
      .catch(() => setNomina([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido]);

  const candidatos = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (q.length < 2) return [];
    return nomina
      .filter((p) =>
        [p.apellido, p.nombres, p.dni, p.legajo]
          .map((v) => String(v ?? "").toLowerCase())
          .some((v) => v.includes(q)),
      )
      .slice(0, 8);
  }, [nomina, buscar]);

  // Una fila por empleado, con su estado del año. Las vacaciones tomadas se
  // agrupan por DNI; la antigüedad sale de la nómina.
  const porPersona = useMemo(() => {
    if (tomadas === undefined) return [];
    const porDni = new Map();
    for (const t of tomadas) {
      const k = String(t.dni);
      if (!porDni.has(k)) porDni.set(k, []);
      porDni.get(k).push(t);
    }
    return nomina
      .map((p) => {
        const suyas = porDni.get(String(p.dni)) || [];
        return {
          dni: String(p.dni),
          nombre: `${p.apellido}, ${p.nombres}`,
          ingreso: p.ingreso_computable,
          anios: antiguedadAlCierre(p.ingreso_computable, anio),
          tomadas: suyas,
          ...estadoDe(p.ingreso_computable, suyas, anio),
        };
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [nomina, tomadas, anio]);

  const vis = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return porPersona;
    return porPersona.filter(
      (p) => p.nombre.toLowerCase().includes(q) || p.dni.includes(q),
    );
  }, [porPersona, filtro]);

  const guardar = async (e) => {
    e.preventDefault();
    const d = dni.replace(/\D/g, "");
    if (!d) return setEstado("mal:Elegí a la persona (buscala o escribí el DNI).");
    const dias = diasCorridos(desde, hasta);
    if (!dias)
      return setEstado("mal:Revisá las fechas: «hasta» no puede ser antes que «desde».");
    setEstado("yendo");
    try {
      await crearVacacion({ dni: d, anio, desde, hasta, dias, nota });
      setEstado("");
      setDni("");
      setBuscar("");
      setDesde("");
      setHasta("");
      setNota("");
      cargar();
    } catch (err) {
      setEstado(
        "mal:" +
          (/policy|permission|denied|violates/i.test(err?.message || "")
            ? "No se cargó: no tenés permiso."
            : `No se pudo guardar: ${err?.message || "error"}`),
      );
    }
  };

  const alternarOk = async (t) => {
    try {
      await confirmarVacacion(t.id, !t.confirmado);
      cargar();
    } catch {
      setEstado("mal:No se pudo cambiar el OK. Probá de nuevo.");
    }
  };

  const anular = async (id) => {
    try {
      await anularVacacion(id);
      cargar();
    } catch {
      setEstado("mal:No se pudo anular. Probá de nuevo en un rato.");
    }
  };

  const diasPropuestos = diasCorridos(desde, hasta);

  if (permisos !== null && !permitido) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      <p className="panel-aviso suave">
        Días que corresponden por antigüedad al 31/12/{anio} (CCT 40/89, art.
        46): hasta 5 años 14 · 5 a 10 21 · 10 a 20 28 · más de 20 35. El primer
        año incompleto va por <b>proporcional</b> (1 día cada 20 trabajados,
        art. 151 LCT) y se marca <span className="pill gris">prop.</span> Sólo
        descuentan las vacaciones tildadas <b>OK</b>.
      </p>

      {puedeCargar && (
        <p className="sub">
          ¿Tenés la planilla de vacaciones?{" "}
          <Link className="rto-link" to="/rrhh/vacaciones/importar">
            Cargar vacaciones desde el .xls →
          </Link>
        </p>
      )}

      {puedeCargar && (
        <form className="tk-filtros" onSubmit={guardar} style={{ rowGap: 8 }}>
          <div style={{ position: "relative" }}>
            <input
              type="search"
              placeholder="Empleado (nombre, DNI o legajo)"
              value={buscar}
              onChange={(e) => {
                setBuscar(e.target.value);
                setDni("");
              }}
              aria-label="Buscar al empleado"
            />
            {!dni && candidatos.length > 0 && (
              <div className="lic-sugeridos">
                {candidatos.map((c) => (
                  <button
                    key={c.dni}
                    type="button"
                    onClick={() => {
                      setDni(String(c.dni));
                      setBuscar(`${c.apellido}, ${c.nombres}`);
                    }}
                  >
                    {c.apellido}, {c.nombres}{" "}
                    <span className="sub">
                      DNI {c.dni} · {c.destino || "sin base"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <label className="sub">
            desde{" "}
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label className="sub">
            hasta{" "}
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
          {diasPropuestos ? (
            <span className="pill gris">{diasPropuestos} días corridos</span>
          ) : null}
          <input
            type="text"
            placeholder="Nota (opcional)"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            aria-label="Nota"
          />
          <button type="submit" className="btn-primario" disabled={estado === "yendo"}>
            {estado === "yendo" ? "Guardando…" : "Cargar vacaciones"}
          </button>
          {estado.startsWith("mal:") && (
            <span className="pill mal">{estado.slice(4)}</span>
          )}
        </form>
      )}

      {!puedeCargar && (
        <p className="panel-aviso suave">
          Podés mirar, pero no cargar.
        </p>
      )}

      {porPersona.length > 0 && (
        <div className="tk-filtros" style={{ marginTop: 8 }}>
          <input
            type="search"
            placeholder="Filtrar la lista por nombre o DNI…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            aria-label="Filtrar vacaciones por nombre o DNI"
            style={{ minWidth: 260 }}
          />
        </div>
      )}

      {tomadas !== undefined && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Empleado</th>
                <th>Antigüedad</th>
                <th>Corresponden</th>
                <th>Tomados</th>
                <th>Pendientes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {vis.length === 0 && (
                <tr>
                  <td colSpan={6} className="tenue">
                    {porPersona.length === 0
                      ? "No hay empleados en la nómina activa."
                      : `Ningún empleado coincide con «${filtro.trim()}».`}
                  </td>
                </tr>
              )}
              {vis.map((p) => (
                <BloquePersona
                  key={p.dni}
                  p={p}
                  abierto={abierto === p.dni}
                  onAbrir={() => setAbierto(abierto === p.dni ? null : p.dni)}
                  puedeCargar={puedeCargar}
                  onOk={alternarOk}
                  onAnular={anular}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/* La fila de un empleado + el detalle de sus períodos. Los pendientes en rojo
 * cuando tomó de más (número negativo): es justo lo que hay que mirar. */
const BloquePersona = ({ p, abierto, onAbrir, puedeCargar, onOk, onAnular }) => {
  const vigentes = p.tomadas.filter((t) => !t.anulado_en);
  const negativo = p.pendientes !== null && p.pendientes < 0;
  return (
    <>
      <tr className={negativo ? "mal" : ""}>
        <td>
          <Link className="rto-link" to={rutaDePersona(p.dni)}>
            {p.nombre}
          </Link>
        </td>
        <td>{p.anios === null ? <span className="tenue">—</span> : `${p.anios} años`}</td>
        <td>
          {p.corresponden === null ? (
            <span className="tenue">—</span>
          ) : (
            <>
              {p.corresponden} días
              {p.proporcional && (
                <span
                  className="pill gris"
                  style={{ marginLeft: 6 }}
                  title="Primer año incompleto: proporcional (1 día cada 20 trabajados, art. 151 LCT), no la escala entera."
                >
                  prop.
                </span>
              )}
            </>
          )}
        </td>
        <td><b>{p.tomados}</b></td>
        <td>
          {p.pendientes === null ? (
            <span className="tenue">—</span>
          ) : negativo ? (
            <span className="pill mal">tomó {-p.pendientes} de más</span>
          ) : (
            <b>{p.pendientes}</b>
          )}
        </td>
        <td className="agenda-acc">
          <button className="tk-boton tk-boton--chico tk-boton--flojo" onClick={onAbrir}>
            {abierto ? "Cerrar" : `Ver ${vigentes.length}`}
          </button>
        </td>
      </tr>
      {abierto && (
        <tr>
          <td colSpan={6}>
            <div className="tk-item-meta" style={{ padding: "6px 0" }}>
              {vigentes.length === 0 && <em>Sin vacaciones cargadas este año.</em>}
              {vigentes
                .slice()
                .sort((a, b) => (String(a.desde) < String(b.desde) ? 1 : -1))
                .map((t) => (
                  <div key={t.id} style={{ padding: "3px 0" }}>
                    {fmt(t.desde)} → {fmt(t.hasta)} · <b>{t.dias} días</b>
                    {t.nota ? ` · ${t.nota}` : ""}{" "}
                    {t.confirmado ? (
                      <span className="pill ok">OK</span>
                    ) : (
                      <span className="pill gris">sin confirmar (no descuenta)</span>
                    )}
                    {puedeCargar && (
                      <>
                        <button
                          className="tk-boton tk-boton--chico"
                          style={{ marginLeft: 8 }}
                          onClick={() => onOk(t)}
                        >
                          {t.confirmado ? "Sacar OK" : "Tildar OK"}
                        </button>
                        <button
                          className="tk-boton tk-boton--chico tk-boton--flojo"
                          style={{ marginLeft: 8 }}
                          onClick={() => onAnular(t.id)}
                        >
                          Anular
                        </button>
                      </>
                    )}
                  </div>
                ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default Vacaciones;
